import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ScrollProgress from '../components/ScrollProgress';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Journeys() {
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('list'); // list | detail
  const [journeys, setJourneys] = useState([]);
  const [progressMap, setProgressMap] = useState({}); // {journey_id: progress}
  const [current, setCurrent] = useState(null);
  const [currentDays, setCurrentDays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [jr, pr] = await Promise.all([
          axios.get(`${API}/api/journeys`),
          axios.get(`${API}/api/journeys/my-progress`, { headers: authHeader() }),
        ]);
        setJourneys(jr.data.journeys || []);
        const map = {};
        (pr.data.progress || []).forEach(p => { map[p.journey_id] = p; });
        setProgressMap(map);
      } catch { toast.error('Could not load journeys.'); }
      finally { setLoading(false); }
    })();
  }, []);

  const openJourney = async (j) => {
    try {
      const r = await axios.get(`${API}/api/journeys/${j.id}`, { headers: authHeader() });
      setCurrent(r.data.journey);
      setCurrentDays(r.data.journey.days || []);
      setProgressMap(prev => ({ ...prev, [j.id]: r.data.progress || prev[j.id] || null }));
      setView('detail');
      window.scrollTo(0, 0);
    } catch { toast.error('Could not open journey.'); }
  };

  const enroll = async () => {
    try {
      const r = await axios.post(`${API}/api/journeys/${current.id}/enroll`, {}, { headers: authHeader() });
      setProgressMap(prev => ({ ...prev, [current.id]: r.data.progress }));
      toast.success('You\'re in. Day 1 awaits.');
    } catch { toast.error('Could not enrol.'); }
  };

  const unenroll = async () => {
    try {
      await axios.delete(`${API}/api/journeys/${current.id}/enroll`, { headers: authHeader() });
      setProgressMap(prev => { const { [current.id]: _, ...rest } = prev; return rest; });
      toast.success('Removed from your journeys.');
    } catch { toast.error('Could not remove.'); }
  };

  const completeDay = async (day) => {
    try {
      const r = await axios.post(`${API}/api/journeys/${current.id}/complete-day`, { day }, { headers: authHeader() });
      setProgressMap(prev => ({ ...prev, [current.id]: r.data.progress }));
      if (r.data.progress.completed_at) {
        toast.success('Journey complete. You showed up — every single day.');
      } else {
        toast.success(`Day ${day} marked complete.`);
      }
    } catch { toast.error('Could not save progress.'); }
  };

  const askVivi = (seed) => {
    try { sessionStorage.setItem('vivi_seed', seed); } catch (e) {}
    navigate('/chat');
  };

  if (loading) return <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="gold-loader" /></div>;

  // ── DETAIL VIEW ──────────────────────────────────────────
  if (view === 'detail' && current) {
    const progress = progressMap[current.id];
    const completedSet = new Set((progress?.completed_days) || []);
    const pct = progress ? Math.round((completedSet.size / current.duration_days) * 100) : 0;
    return (
      <div className="journey-detail-shell" style={{ minHeight: '100vh', paddingBottom: 'var(--bottom-h)', position: 'relative' }} data-testid="journey-detail">
        <ScrollProgress />
        <div className="education-aurora" aria-hidden="true" />
        <div style={{ padding: '18px 16px 8px', position: 'relative', zIndex: 1 }}>
          <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '0.79rem', letterSpacing: '0.15em', padding: 0, marginBottom: 8 }} data-testid="journey-back">
            <i className="fa-solid fa-arrow-left" /> ALL JOURNEYS
          </button>
          <div className="eyebrow" style={{ color: current.color }}>Intimacy Journey · {current.duration_days} days</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.25rem', marginBottom: 4 }}>{current.title}</div>
          <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', color: 'var(--gold)', fontSize: '1.16rem', marginBottom: 12 }}>{current.tagline}</p>
          <p style={{ fontSize: '1.01rem', color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: 16 }}>{current.description}</p>

          {progress ? (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.73rem', letterSpacing: '0.12em', color: 'var(--text-dim)', marginBottom: 6 }}>
                <span>PROGRESS</span><span>{completedSet.size} / {current.duration_days} DAYS · {pct}%</span>
              </div>
              <div style={{ height: 4, background: 'var(--bg2)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${current.color}, var(--gold))`, transition: 'width 0.6s ease' }} />
              </div>
              <button onClick={unenroll} style={{ marginTop: 12, background: 'none', border: '1px solid var(--border-soft)', color: 'var(--text-dim)', padding: '6px 14px', fontSize: '0.73rem', letterSpacing: '0.12em', cursor: 'pointer', fontFamily: 'var(--font-ui)' }} data-testid="journey-unenroll">
                LEAVE JOURNEY
              </button>
            </div>
          ) : (
            <button onClick={enroll} className="btn-gold w-full" style={{ marginBottom: 14, fontSize: '0.83rem' }} data-testid="journey-enroll">
              <i className="fa-solid fa-play" /> BEGIN THIS JOURNEY
            </button>
          )}
        </div>

        <div style={{ padding: '8px 16px 0', position: 'relative', zIndex: 1 }}>
          {currentDays.map((d, idx) => {
            const done = completedSet.has(d.day);
            const locked = !progress;
            return (
              <motion.div
                key={d.day}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.02 }}
                className="journey-day-card"
                style={{
                  padding: '14px 14px',
                  marginBottom: 8,
                  border: `1px solid ${done ? current.color : 'var(--border-soft)'}`,
                  background: done ? `linear-gradient(135deg, ${current.color}18, transparent)` : 'var(--bg2)',
                  position: 'relative',
                  opacity: locked ? 0.65 : 1,
                }}
                data-testid={`journey-day-${d.day}`}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div style={{ fontFamily: 'var(--font-ui)', fontSize: '0.71rem', letterSpacing: '0.18em', color: done ? current.color : 'var(--text-dim)', fontWeight: 700 }}>
                    DAY {d.day}{done ? ' · COMPLETE' : ''}
                  </div>
                  {!locked && (
                    <button onClick={() => completeDay(d.day)} title={done ? 'Toggle' : 'Mark complete'} style={{ background: 'none', border: 'none', color: done ? current.color : 'var(--text-dim)', cursor: 'pointer', fontSize: '1.03rem' }} data-testid={`journey-complete-${d.day}`}>
                      <i className={`fa-${done ? 'solid' : 'regular'} fa-circle-check`} />
                    </button>
                  )}
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', marginBottom: 4 }}>{d.title}</div>
                <p style={{ fontSize: '1.01rem', color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: d.vivi_seed ? 8 : 0 }}>{d.prompt}</p>
                {d.vivi_seed && !locked && (
                  <button onClick={() => askVivi(d.vivi_seed)} style={{
                    fontSize: '0.75rem', letterSpacing: '0.1em', color: 'var(--gold)',
                    background: 'var(--gold-dim)', border: '1px solid var(--border)',
                    padding: '5px 10px', cursor: 'pointer', fontFamily: 'var(--font-ui)',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }} data-testid={`journey-vivi-${d.day}`}>
                    <i className="fa-solid fa-comment-dots" /> ASK VIVI
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────
  return (
    <div className="journeys-list-shell" style={{ minHeight: '100vh', paddingBottom: 'var(--bottom-h)', position: 'relative' }} data-testid="journeys-list">
      <div className="education-aurora" aria-hidden="true" />
      <div style={{ padding: '18px 16px 8px', position: 'relative', zIndex: 1 }}>
        <div className="eyebrow">Structured Transformations</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.35rem', marginBottom: 4 }}>Intimacy <span className="gold-italic">Journeys</span></div>
        <p style={{ fontFamily: 'var(--font-ui)', fontSize: '1.01rem', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.6 }}>
          {journeys.length} guided programs · daily rituals · VIVI on every step
        </p>
      </div>

      <motion.div
        className="journeys-grid"
        style={{ padding: '0 16px 0', position: 'relative', zIndex: 1, display: 'grid', gap: 10 }}
        variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05, delayChildren: 0.08 } } }}
        initial="hidden"
        animate="show"
      >
        {journeys.map(j => {
          const p = progressMap[j.id];
          const pct = p ? Math.round((p.completed_days.length / j.duration_days) * 100) : 0;
          return (
            <motion.button
              key={j.id}
              onClick={() => openJourney(j)}
              variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.34, ease: 'easeOut' } } }}
              whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}
              className="immersive-card"
              style={{
                background: 'var(--bg2)',
                border: `1px solid ${p ? j.color : 'var(--border-soft)'}`,
                padding: '14px 16px',
                cursor: 'pointer',
                textAlign: 'left',
                position: 'relative',
                color: 'var(--text)',
              }}
              data-testid={`journey-card-${j.id}`}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{
                  width: 44, height: 44, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `${j.color}22`,
                  border: `1px solid ${j.color}55`,
                }}>
                  <i className={`fa-solid ${j.icon}`} style={{ color: j.color, fontSize: '1.2rem' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.69rem', letterSpacing: '0.18em', color: 'var(--text-dim)', marginBottom: 3, fontWeight: 700 }}>
                    {j.duration_days} DAYS · {j.audience.toUpperCase()}
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.32rem', lineHeight: 1.25, marginBottom: 3 }}>{j.title}</div>
                  <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: '1.01rem', color: 'var(--gold)', opacity: 0.95, lineHeight: 1.45, marginBottom: p ? 8 : 0 }}>{j.tagline}</p>
                  {p && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.69rem', letterSpacing: '0.12em', color: 'var(--text-dim)', marginBottom: 4 }}>
                        <span>IN PROGRESS</span><span>{p.completed_days.length}/{j.duration_days} · {pct}%</span>
                      </div>
                      <div style={{ height: 3, background: 'var(--bg3)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${j.color}, var(--gold))` }} />
                      </div>
                    </>
                  )}
                </div>
                <i className="fa-solid fa-chevron-right" style={{ color: 'var(--text-dim)', alignSelf: 'center', fontSize: '0.79rem' }} />
              </div>
            </motion.button>
          );
        })}
      </motion.div>
    </div>
  );
}
